import { useMemo } from 'react';
import { SortOrder } from 'src/types/enum/sort-order';
import { BookingStatistic } from '../types/booking-statistic';
import { BookingStatisticFiltered } from '../types/booking-statistic-filtered';
import { Filters } from 'src/types/interfaces/filters';
import { ContentType } from '../types/content-type';

export function useFilteredAndSortedStatisticsData(
  data: BookingStatistic[],
  sortBy: string,
  order: SortOrder.ASC | SortOrder.DESC,
  contentType: ContentType,
  filters: Filters,
): {
  data: BookingStatisticFiltered[];
} {
  const preparedData = useMemo(() => {
    const direction = order === SortOrder.ASC ? 1 : -1;

    return [...data]
      .map(item => {
        if (
          filters.floors &&
          Array.isArray(filters.floors.value) &&
          filters.floors.value.length > 0
        ) {
          const selectedFloors = filters.floors.value.map(floor =>
            Number(floor),
          );

          let DESK = 0;
          let ROOM = 0;

          selectedFloors.forEach(floor => {
            const floorData = item.spaceFloors[floor];
            if (floorData) {
              DESK += floorData.DESK;
              ROOM += floorData.ROOM;
            }
          });

          return { ...item, _filteredDESK: DESK, _filteredROOM: ROOM };
        }

        return {
          ...item,
          _filteredDESK: item.DESK,
          _filteredROOM: item.ROOM,
        };
      })
      .sort((a, b) => {
        let aValue:
          | string
          | number
          | { [key: number]: { ROOM: number; DESK: number } };
        let bValue:
          | string
          | number
          | { [key: number]: { ROOM: number; DESK: number } };

        if (sortBy === 'date') {
          aValue = a.date;
          bValue = b.date;
        } else {
          const key: string = sortBy.toUpperCase();
          let filteredKey: keyof BookingStatisticFiltered;

          if (key === 'BOOKINGS') {
            filteredKey =
              `_filtered${contentType}` as keyof BookingStatisticFiltered;
          } else {
            filteredKey = `_filtered${key}` as keyof BookingStatisticFiltered;
          }

          aValue = a[filteredKey];
          bValue = b[filteredKey];
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return (aValue - bValue) * direction;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return aValue.localeCompare(bValue) * direction;
        }

        return 0;
      })
      .filter(item =>
        contentType === 'GENERAL'
          ? item._filteredDESK !== 0 || item._filteredROOM !== 0
          : contentType === 'DESK'
            ? item._filteredDESK !== 0
            : item._filteredROOM !== 0,
      );
  }, [data, sortBy, order, filters, contentType]);

  return {
    data: preparedData,
  };
}
